import React, { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {
  List,
  TextField,
  DateField,
  FunctionField,
  ReferenceField,
  useDataProvider,
  useNotify,
  TopToolbar,
  Button as RAButton,
  useListContext,
  ReferenceInput,
  SelectInput,
  ExportButton
} from 'react-admin';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ja';
import jsonExport from 'jsonexport/dist';

import FlexibleListLayout from '../FlexibleListLayout';
import YenField from '../YenField';
import downloadCSVCompat from '../../utils/downloadCSVCompat';
import { formatRFC3339DateOnlyString } from '../../utils/dateutils';
import { MAX_PER_PAGE } from '../../constants/define';
import { PaymentDetail, PaymentTypeLabels, PaymentStatusLabels } from './PaymentTypes';
import { PaymentStatusChip } from './PaymentStatusChip';
import { PaymentStatusButton } from './PaymentStatusButton';
import { BulkPaymentStatusButtons } from './BulkPaymentStatusButtons';

interface PaymentDetailsListProps {
  paymentId: number;
  paymentDate?: string;
  apiBasePath?: string;
  navigateBasePath?: string;
  showZenginExport?: boolean;
  canEditStatus?: boolean;
  showBulkActions?: boolean;
  ecSiteLinkEnabled?: boolean;
  partnerReference?: 'partners' | 'affiliates';
  partnerLinkBasePath?: string;
  filterPartnerReference?: string;
  showEcSiteFilter?: boolean;
  showPartnerFilter?: boolean;
  showEcSiteColumns?: boolean;
  title?: string;
}

// List actions
const PaymentListActions = ({
  paymentId,
  paymentDate,
  exporter,
  showZenginExport = true,
  apiBasePath = 'payments'
}: {
  paymentId?: number;
  paymentDate?: string;
  exporter?: (records: any[]) => void;
  showZenginExport?: boolean;
  apiBasePath?: string;
}) => {
  const { filterValues, total, data } = useListContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);

  const handleZenginExport = async (exportDate: string) => {
    if (!paymentId) return;

    try {
      // フィルター条件を構築（ページングとソートは除外）
      const filterConditions: any = {};

      if (filterValues.clientId) {
        filterConditions.clientId = filterValues.clientId;
      }

      if (filterValues.partnerId) {
        filterConditions.partnerId = filterValues.partnerId;
      }

      if (filterValues.paymentStatus) {
        filterConditions.paymentStatus = filterValues.paymentStatus;
      }

      const API_URL = import.meta.env.VITE_SIMPLE_REST_URL;
      let url = `${API_URL}/${apiBasePath}/export/${paymentId}`;

      // パラメータを構築
      const params = new URLSearchParams();
      if (Object.keys(filterConditions).length > 0) {
        params.append('filter', JSON.stringify(filterConditions));
      }
      params.append('date', exportDate);

      url += `?${params.toString()}`;

      await dataProvider.download(url, {});
      notify('全銀データをダウンロードしました', { type: 'success' });
    } catch (error) {
      notify('全銀データのダウンロードに失敗しました', { type: 'error' });
      console.error('Failed to export zengin data:', error);
    }
  };

  const handleOpenDateDialog = () => {
    const defaultDate = paymentDate ? dayjs(paymentDate) : dayjs();
    setSelectedDate(defaultDate);
    setDateDialogOpen(true);
  };

  const handleCloseDateDialog = () => {
    setDateDialogOpen(false);
  };

  const handleConfirmExport = () => {
    if (selectedDate) {
      handleZenginExport(selectedDate.format('YYYY-MM-DD'));
      setDateDialogOpen(false);
    }
  };

  const today = dayjs();
  const minDate = today;
  const maxDate = today.add(1, 'year').subtract(1, 'day');

  // 未振り込み件数をカウント（paymentStatus === 2）
  const unpaidCount = data ? data.filter((record: PaymentDetail) => record.paymentStatus === 2).length : 0;

  return (
    <>
      <TopToolbar>
        {showZenginExport && (
          <RAButton label="全銀データ出力" onClick={handleOpenDateDialog} disabled={!paymentId || unpaidCount === 0}>
            <DownloadIcon />
          </RAButton>
        )}
        <ExportButton label="CSVダウンロード" exporter={exporter} disabled={total === 0} maxResults={MAX_PER_PAGE} />
      </TopToolbar>

      {/* 日付入力ダイアログ */}
      <Dialog open={dateDialogOpen} onClose={handleCloseDateDialog}>
        <DialogTitle>全銀データ出力</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, minWidth: 300 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
              <DatePicker
                label="支払日"
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                minDate={minDate}
                maxDate={maxDate}
                format="YYYY/MM/DD"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: '支払日を指定してください（本日以降1年未満）'
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDateDialog}>キャンセル</Button>
          <Button
            onClick={handleConfirmExport}
            variant="contained"
            disabled={!selectedDate || !selectedDate.isValid() || selectedDate.isBefore(today, 'day') || selectedDate.isAfter(maxDate, 'day')}
          >
            出力
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const PaymentDetailsList: React.FC<PaymentDetailsListProps> = ({
  paymentId,
  paymentDate,
  apiBasePath = 'payments',
  navigateBasePath = '/payments',
  showZenginExport = true,
  canEditStatus = true,
  showBulkActions = true,
  ecSiteLinkEnabled = true,
  partnerReference = 'partners',
  partnerLinkBasePath = '/partners',
  filterPartnerReference,
  showEcSiteFilter = true,
  showPartnerFilter = true,
  showEcSiteColumns = true,
  title = '支払い管理'
}) => {
  const { selectedIds = [] } = useListContext() || {};
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dataProvider = useDataProvider();

  // ソート後にpaymentIdが失われた場合の復元処理
  useEffect(() => {
    const paymentIdParam = searchParams.get('paymentId');
    if (!paymentIdParam && paymentId) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('paymentId', paymentId.toString());
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, paymentId, setSearchParams]);

  const handleExport = async (records: any[]) => {
    if (!records || records.length === 0) {
      return;
    }

    let exportFields = [
      'paymentDate',
      'clientId',
      'clientName',
      'affiliateStatusFromPaymentType',
      'partnerName',
      'paymentType',
      'amount',
      'currentRewardAmount',
      'carryoverAmount',
      'administrativeFee',
      'totalAmount',
      'pendingAmount',
      'forfeitedAmount',
      'paymentStatus'
    ];

    let headers = [
      '支払予定日',
      'ショップID',
      'ECサイト',
      'アフィリエイトステータス',
      'パートナー',
      '支払種別',
      '報酬額',
      '今回報酬額',
      '繰越金額',
      '事務手数料',
      '支払額',
      '保留額',
      '失効額',
      '支払ステータス'
    ];

    if (!showEcSiteColumns) {
      const excludeFields = ['clientId', 'clientName'];
      const excludeIndices = exportFields
        .map((field, index) => excludeFields.includes(field) ? index : -1)
        .filter(index => index !== -1);
      exportFields = exportFields.filter((_, index) => !excludeIndices.includes(index));
      headers = headers.filter((_, index) => !excludeIndices.includes(index));
    }

    const dataToExport = records.map((record) => {
      const transformedRecord: Record<string, any> = {};

      exportFields.forEach((key, index) => {
        let value = record[key];

        if (key === 'paymentDate' && value) {
          value = formatRFC3339DateOnlyString(value);
        }

        if (key === 'affiliateStatusFromPaymentType') {
          value = record.paymentType === 1 ? '有効' : '無効';
        }

        if (key === 'paymentType') {
          value = PaymentTypeLabels[value] || '';
        }

        if (key === 'paymentStatus') {
          value = PaymentStatusLabels[value] || '';
        }

        const numericFields = [
          'amount',
          'currentRewardAmount',
          'carryoverAmount',
          'administrativeFee',
          'totalAmount',
          'pendingAmount',
          'forfeitedAmount'
        ];
        let finalValue;
        if (numericFields.includes(key)) {
          finalValue = value !== null && value !== undefined ? value.toLocaleString() : '';
        } else {
          finalValue = value || '';
        }
        transformedRecord[headers[index]] = finalValue;
      });

      return transformedRecord;
    });

    jsonExport(dataToExport, { headers }, (err: Error | null, csv: string) => {
      if (err) {
        console.error(err);
        return;
      }
      const bom = '\uFEFF';
      const csvWithBom = bom + csv;
      downloadCSVCompat(csvWithBom, '支払い詳細');
    });
  };

  // フィルターコンポーネントを動的に構築
  const PaymentFilters = [];

  if (showEcSiteFilter) {
    PaymentFilters.push(
      <ReferenceInput key="clientId" source="clientId" reference="clients" alwaysOn perPage={MAX_PER_PAGE}>
        <SelectInput optionText="name" label={'ECサイト'} emptyText="(選択解除)" />
      </ReferenceInput>
    );
  }

  if (showPartnerFilter) {
    PaymentFilters.push(
      <ReferenceInput key="partnerId" source="partnerId" reference={filterPartnerReference || `references/${partnerReference}`} alwaysOn perPage={MAX_PER_PAGE}>
        <SelectInput optionText="name" label={'パートナー'} emptyText="(選択解除)" />
      </ReferenceInput>
    );
  }

  PaymentFilters.push(
    <SelectInput
      key="paymentStatus"
      source="paymentStatus"
      label="支払ステータス"
      choices={[
        { id: 1, name: '保留' },
        { id: 2, name: '未振込' },
        { id: 3, name: '振込済み' },
        { id: 4, name: '失効' },
        { id: 99, name: 'エラー' }
      ]}
      alwaysOn
      sx={{ minWidth: 200 }}
      emptyText="(選択解除)"
    />
  );

  const handleDetailClick = (recordId: number) => {
    const paymentIdParam = searchParams.get('paymentId');
    const queryString = paymentIdParam ? `?paymentId=${paymentIdParam}` : '';
    navigate(`${navigateBasePath}/${recordId}${queryString}`);
  };

  // 詳細ボタンコンポーネント
  const PaymentDetailButton = () => {
    const record = useListContext().data?.find?.((r: any) => r.id === selectedIds[0]) as PaymentDetail | undefined;

    // useListContextではなく、useRecordContextを使うべきだが、ここでは簡易的に実装
    return null; // この関数は使わない
  };

  return (
    <List
      resource={`${apiBasePath}/details/${paymentId}`}
      actions={
        <PaymentListActions
          paymentId={paymentId}
          paymentDate={paymentDate}
          exporter={handleExport}
          showZenginExport={showZenginExport}
          apiBasePath={apiBasePath}
        />
      }
      sort={{ field: 'id', order: 'ASC' }}
      title={title}
      filters={PaymentFilters}
      exporter={handleExport}
      empty={
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            支払いはありません。
          </Typography>
        </Box>
      }
    >
      <FlexibleListLayout
        bulkActionButtons={showBulkActions ? <BulkPaymentStatusButtons selectedIds={selectedIds} apiBasePath={apiBasePath} /> : false}
        rowClick={false}
      >
        <DateField source="paymentDate" label="支払予定日" />
        {showEcSiteColumns && <TextField source="clientId" label="ショップID" />}
        {showEcSiteColumns && (ecSiteLinkEnabled ? (
          <ReferenceField
            source="clientId"
            reference="references/clients"
            label="ECサイト"
            sortBy="clientName"
            link={(record: any) => `/clients/${record.id}/show`}
          >
            <TextField source="name" />
          </ReferenceField>
        ) : (
          <TextField source="clientName" label="ECサイト" sortBy="clientName" />
        ))}
        <FunctionField
          source="paymentType"
          label="アフィリエイトステータス"
          sortBy="paymentType"
          render={(record: PaymentDetail) => (record.paymentType === 1 ? '有効' : '無効')}
        />
        <ReferenceField
          source={partnerReference === 'affiliates' ? 'affiliateId' : 'partnerId'}
          reference={partnerReference === 'affiliates' ? 'affiliates' : `references/${partnerReference}`}
          label="パートナー"
          sortBy="partnerName"
          link={(record: any) => `${partnerLinkBasePath}/${record.id}/show`}
        >
          <TextField source="name" />
        </ReferenceField>
        <FunctionField
          source="paymentType"
          label="支払種別"
          render={(record: PaymentDetail) => PaymentTypeLabels[record.paymentType] || ''}
        />
        <YenField source="amount" label="報酬額" />
        <YenField source="currentRewardAmount" label="今回報酬額" />
        <YenField source="carryoverAmount" label="繰越金額" />
        <YenField source="administrativeFee" label="事務手数料" />
        <YenField source="totalAmount" label="支払額" />
        <YenField source="pendingAmount" label="保留額" />
        <YenField source="forfeitedAmount" label="失効額" />
        <FunctionField
          source="paymentStatus"
          label="支払ステータス"
          render={(record: PaymentDetail) => <PaymentStatusChip record={record} />}
        />
        <PaymentStatusButton canEdit={canEditStatus} apiBasePath={apiBasePath} />
        <FunctionField
          label=""
          render={(record: PaymentDetail) => (
            <Button variant="outlined" onClick={() => handleDetailClick(record.id)} size="small">
              詳細
            </Button>
          )}
        />
      </FlexibleListLayout>
    </List>
  );
};

export default PaymentDetailsList;
